<template>
  <div class="gl-customers-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-branch-header">
        <div>
          <div class="gl-branch-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Customers</span>
          </div>
          <h1 class="gl-branch-title">Customers</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Stats strip -->
      <div class="gl-stats-strip">
        <div class="gl-stat-chip">
          <span class="gl-stat-num">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats ? stats.total_customers : '—' }}</span>
          </span>
          <span class="gl-stat-lbl">Total Customers</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num" style="color:#900C27">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats ? stats.new_customers_this_month : '—' }}</span>
          </span>
          <span class="gl-stat-lbl">New This Month</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num" style="color:#2563eb">
            <span v-if="statsLoading" class="gl-skel-num"></span>
            <span v-else>{{ stats ? stats.active_customers : '—' }}</span>
          </span>
          <span class="gl-stat-lbl">Active (30d)</span>
        </div>
        <div class="gl-stat-divider"></div>
        <div class="gl-stat-chip">
          <span class="gl-stat-num" style="color:#d97706">
            <span v-if="statsLoading" class="gl-skel-num gl-skel-num-wide"></span>
            <span v-else>{{ stats ? 'RM ' + formatPrice(stats.total_revenue) : '—' }}</span>
          </span>
          <span class="gl-stat-lbl">Total Revenue</span>
        </div>
      </div>

      <!-- Filter bar -->
      <div class="gl-filter-bar">
        <div class="gl-search-wrap">
          <svg class="gl-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            class="gl-search-input"
            v-model="filters.search"
            placeholder="Search by name, phone or email…"
            @input="debouncedSearch"
          >
        </div>
        <select class="gl-filter-select" v-model="filters.membership_tier_id" @change="fetchCustomers">
          <option value="">All Membership Tiers</option>
          <option v-for="tier in tiers" :key="tier.id" :value="tier.id">{{ tier.name }}</option>
        </select>
        <select class="gl-filter-select" v-model="filters.sort_by" @change="fetchCustomers">
          <option value="orders_count">Sort by Orders</option>
          <option value="name">Sort by Name</option>
          <option value="created_at">Sort by Join Date</option>
          <option value="last_login_at">Sort by Last Login</option>
        </select>
        <button class="gl-filter-clear" @click="resetFilters" v-if="filters.search || filters.membership_tier_id || filters.sort_by !== 'orders_count'">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Clear
        </button>
      </div>

      <!-- Table card -->
      <div class="gl-table-card">

        <!-- Skeleton -->
        <template v-if="loading">
          <div class="gl-skel-table">
            <div v-for="i in 8" :key="i" class="gl-skel-row" :style="`animation-delay:${i*30}ms`">
              <div class="gl-skel-cust-cell">
                <div class="gl-skel-avatar"></div>
                <div class="gl-skel-cust-text">
                  <div class="gl-skel-line w50"></div>
                  <div class="gl-skel-line w30"></div>
                </div>
              </div>
              <div class="gl-skel-contact">
                <div class="gl-skel-line w40"></div>
                <div class="gl-skel-line w50"></div>
              </div>
              <div class="gl-skel-badge-wrap"><div class="gl-skel-badge"></div></div>
              <div class="gl-skel-line w15"></div>
              <div class="gl-skel-line w30"></div>
              <div class="gl-skel-line w30"></div>
              <div class="gl-skel-line gl-skel-btn-view"></div>
            </div>
          </div>
        </template>

        <template v-else>
          <div v-if="!customers.length" class="gl-empty">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <p>No customers found.</p>
          </div>

          <div v-else class="gl-table-wrap">
            <table class="gl-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Membership</th>
                  <th class="th-center">Orders</th>
                  <th class="th-right">Total Spent</th>
                  <th>Last Active</th>
                  <th class="th-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="customer in customers" :key="customer.id">
                  <td>
                    <div class="gl-cust-cell">
                      <div class="gl-avatar" v-if="customer.avatar">
                        <img :src="customer.avatar" alt="">
                      </div>
                      <div class="gl-avatar gl-avatar-placeholder" v-else>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      </div>
                      <div>
                        <div class="gl-cust-name">{{ customer.name }}</div>
                        <div class="gl-cust-since">Since {{ formatDate(customer.created_at) }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="gl-contact-phone">{{ customer.phone }}</div>
                    <div class="gl-contact-email">{{ customer.email || 'No email' }}</div>
                  </td>
                  <td>
                    <span class="gl-tier-badge" :class="getTierClass(customer.membership_tier?.name)">
                      {{ customer.membership_tier?.name || 'No Tier' }}
                    </span>
                  </td>
                  <td class="td-center">
                    <span class="gl-orders-count">{{ customer.orders_count }}</span>
                  </td>
                  <td class="td-right">
                    <span class="gl-spent-amount">RM {{ formatPrice(customer.total_spent) }}</span>
                  </td>
                  <td>
                    <span v-if="customer.last_login_at" class="gl-last-active">{{ formatRelativeDate(customer.last_login_at) }}</span>
                    <span v-else class="gl-last-never">Never</span>
                  </td>
                  <td class="td-center">
                    <router-link :to="`/owner/customers/${customer.id}`" class="gl-act-view">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      View
                    </router-link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="pagination.last_page > 1" class="gl-pagination">
            <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchCustomers(pagination.current_page - 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              class="gl-page-btn"
              :class="{ active: page === pagination.current_page }"
              @click="fetchCustomers(page)"
            >{{ page }}</button>
            <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchCustomers(pagination.current_page + 1)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </template>

      </div>

      <!-- Bottom analytics section -->
      <div class="gl-bottom-grid" v-if="stats && stats.top_customers && stats.top_customers.length">

        <!-- Top customers -->
        <div class="gl-bottom-card">
          <div class="gl-bottom-card-header">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Top Customers by Spending
          </div>
          <div class="gl-bottom-card-body">
            <table class="gl-compact-table">
              <thead>
                <tr>
                  <th class="th-rank">#</th>
                  <th>Customer</th>
                  <th class="th-center">Orders</th>
                  <th class="th-right">Total Spent</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(customer, index) in stats.top_customers" :key="customer.id">
                  <td class="td-rank">
                    <span class="gl-rank" :class="index < 3 ? `gl-rank-${index+1}` : ''">{{ index + 1 }}</span>
                  </td>
                  <td>
                    <router-link :to="`/owner/customers/${customer.id}`" class="gl-top-cust-name">{{ customer.name }}</router-link>
                    <div class="gl-top-cust-phone">{{ customer.phone }}</div>
                  </td>
                  <td class="td-center">
                    <span class="gl-orders-count">{{ customer.order_count }}</span>
                  </td>
                  <td class="td-right">
                    <span class="gl-spent-amount">RM {{ formatPrice(customer.total_spent) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Tier breakdown -->
        <div class="gl-bottom-card">
          <div class="gl-bottom-card-header">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Customers by Membership Tier
          </div>
          <div class="gl-bottom-card-body">
            <div v-for="tier in stats.customers_by_tier" :key="tier.tier" class="gl-tier-row">
              <div class="gl-tier-row-top">
                <span class="gl-tier-badge-sm" :class="getTierClass(tier.tier)">{{ tier.tier }}</span>
                <span class="gl-tier-count">{{ tier.count }} customer{{ tier.count !== 1 ? 's' : '' }}</span>
              </div>
              <div class="gl-progress-track">
                <div
                  class="gl-progress-fill"
                  :class="getTierProgressClass(tier.tier)"
                  :style="{ width: getTierPercentage(tier.count) + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import { ownerCustomerApi } from '@/services/ownerApi';

export default {
  name: 'CustomerList',

  data() {
    return {
      loading: true,
      statsLoading: true,
      customers: [],
      tiers: [],
      stats: null,
      searchTimeout: null,
      filters: {
        search: '',
        membership_tier_id: '',
        sort_by: 'orders_count'
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0
      }
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.pagination.last_page;
      const current = this.pagination.current_page;
      const delta = 2;
      for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  mounted() {
    Promise.all([this.fetchStats(), this.fetchTiers()]);
    this.fetchCustomers();
  },

  methods: {
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-MY', { year: 'numeric', month: 'short' });
    },

    formatRelativeDate(date) {
      if (!date) return '';
      const diffDays = Math.floor((new Date() - new Date(date)) / 86400000);
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
      return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
    },

    getTierClass(tierName) {
      const t = (tierName || '').toLowerCase();
      if (t === 'platinum') return 'gl-tier-platinum';
      if (t === 'gold') return 'gl-tier-gold';
      if (t === 'silver') return 'gl-tier-silver';
      return 'gl-tier-none';
    },

    getTierProgressClass(tierName) {
      const t = (tierName || '').toLowerCase();
      if (t === 'platinum') return 'gl-prog-platinum';
      if (t === 'gold') return 'gl-prog-gold';
      if (t === 'silver') return 'gl-prog-silver';
      return 'gl-prog-default';
    },

    getTierPercentage(count) {
      if (!this.stats || this.stats.total_customers === 0) return 0;
      return Math.round((count / this.stats.total_customers) * 100);
    },

    debouncedSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => { this.fetchCustomers(); }, 300);
    },

    resetFilters() {
      this.filters = { search: '', membership_tier_id: '', sort_by: 'orders_count' };
      this.fetchCustomers();
    },

    async fetchTiers() {
      try {
        const response = await ownerCustomerApi.getTiers();
        this.tiers = response.data.data;
      } catch (error) {
        console.error('Failed to fetch tiers:', error);
      }
    },

    async fetchStats() {
      try {
        this.statsLoading = true;
        const response = await ownerCustomerApi.getStats();
        this.stats = response.data.data;
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        this.statsLoading = false;
      }
    },

    async fetchCustomers(page = 1) {
      try {
        this.loading = true;
        const response = await ownerCustomerApi.list({ page, ...this.filters });
        this.customers = response.data.data;
        if (response.data.meta) {
          this.pagination = {
            current_page: response.data.meta.current_page,
            last_page: response.data.meta.last_page,
            per_page: response.data.meta.per_page,
            total: response.data.meta.total
          };
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* ── Page header ── */
.gl-branch-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-branch-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-branch-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-branch-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-branch-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

/* ── Stats strip ── */
.gl-stats-strip {
  display: flex; align-items: center;
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  padding: 12px 18px; margin-bottom: 14px; flex-wrap: wrap; gap: 4px;
}
.gl-stat-chip { display: flex; flex-direction: column; align-items: flex-start; padding: 4px 16px 4px 0; }
.gl-stat-num { font-size: 20px; font-weight: 700; color: #111827; line-height: 1.1; min-height: 24px; display: flex; align-items: center; }
.gl-stat-lbl { font-size: 11px; color: #9ca3af; font-weight: 500; margin-top: 1px; white-space: nowrap; }
.gl-stat-divider { width: 1px; height: 32px; background: #f0f1f3; margin: 0 12px; flex-shrink: 0; }
.gl-skel-num { display: inline-block; width: 36px; height: 18px; background: #f0f1f3; border-radius: 5px; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-num-wide { width: 72px; }
@keyframes gl-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }

/* ── Filter bar ── */
.gl-filter-bar {
  display: flex; align-items: center; gap: 10px;
  background: transparent; border: none;
  padding: 0; margin-bottom: 16px; flex-wrap: wrap;
}
.gl-search-wrap { position: relative; flex: 1; min-width: 220px; }
.gl-search-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.gl-search-input { width: 100%; border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 10px 7px 30px; font-size: 13.5px; color: #111827; outline: none; background: #fff; transition: border-color 0.15s; box-sizing: border-box; }
.gl-search-input:focus { border-color: #900C27; }
.gl-search-input::placeholder { color: #c0c4cc; }
.gl-filter-select { border: 1.5px solid #e8eaf0; border-radius: 9px; padding: 7px 12px; font-size: 13px; color: #374151; background: #fff; outline: none; cursor: pointer; transition: border-color 0.15s; }
.gl-filter-select:focus { border-color: #900C27; }
.gl-filter-clear { display: inline-flex; align-items: center; gap: 5px; padding: 6px 12px; border: 1.5px solid #fecaca; border-radius: 8px; background: #fff5f5; color: #dc2626; font-size: 12.5px; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.gl-filter-clear:hover { background: #fee2e2; }

/* ── Skeleton ── */
.gl-skel-table { padding: 4px 0; }
.gl-skel-row { display: flex; align-items: center; gap: 20px; padding: 14px 16px; border-bottom: 1px solid #f5f6f8; animation: gl-pulse 1.4s ease-in-out infinite; }
.gl-skel-row:last-child { border-bottom: none; }
.gl-skel-cust-cell { display: flex; align-items: center; gap: 10px; flex: 1.4; min-width: 0; }
.gl-skel-avatar { width: 38px; height: 38px; border-radius: 50%; background: #f0f1f3; flex-shrink: 0; }
.gl-skel-cust-text { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.gl-skel-contact { flex: 1; display: flex; flex-direction: column; gap: 5px; }
.gl-skel-badge-wrap { flex-shrink: 0; }
.gl-skel-line { height: 11px; background: #f0f1f3; border-radius: 5px; flex-shrink: 0; }
.gl-skel-badge { width: 62px; height: 20px; background: #f0f1f3; border-radius: 20px; }
.gl-skel-btn-view { width: 60px; height: 28px; background: #f0f1f3; border-radius: 7px; }
.w15 { width: 40px; } .w30 { width: 80px; } .w40 { width: 110px; } .w50 { width: 130px; }

/* ── Table card ── */
.gl-table-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; margin-bottom: 20px; }
.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.gl-table thead tr { background: #f8f9fa; border-bottom: 1.5px solid #f0f1f3; }
.gl-table thead th { padding: 11px 16px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; white-space: nowrap; }
.gl-table thead th.th-center { text-align: center; }
.gl-table thead th.th-right { text-align: right; }
.gl-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #fafafa; }
.gl-table tbody td { padding: 12px 16px; color: #374151; vertical-align: middle; }
.gl-table tbody td.td-center { text-align: center; }
.gl-table tbody td.td-right { text-align: right; }

/* Customer cell */
.gl-cust-cell { display: flex; align-items: center; gap: 10px; }
.gl-avatar { width: 38px; height: 38px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.gl-avatar img { width: 100%; height: 100%; object-fit: cover; }
.gl-avatar-placeholder { background: #f3f4f6; display: flex; align-items: center; justify-content: center; color: #d1d5db; }
.gl-cust-name { font-weight: 600; font-size: 13.5px; color: #111827; }
.gl-cust-since { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }

/* Contact */
.gl-contact-phone { font-size: 13px; color: #374151; font-weight: 500; }
.gl-contact-email { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }

/* Membership tier badge */
.gl-tier-badge { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; white-space: nowrap; }
.gl-tier-platinum { background: #1e293b; color: #e2e8f0; }
.gl-tier-gold     { background: #fef3c7; color: #92400e; }
.gl-tier-silver   { background: #f1f5f9; color: #475569; }
.gl-tier-none     { background: #f3f4f6; color: #9ca3af; }

/* Orders + spent */
.gl-orders-count { font-size: 14px; font-weight: 700; color: #111827; }
.gl-spent-amount { font-size: 13.5px; font-weight: 700; color: #111827; }

/* Last active */
.gl-last-active { font-size: 13px; color: #6b7280; }
.gl-last-never { font-size: 13px; color: #d1d5db; }

/* View button */
.gl-act-view { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border: 1.5px solid #e8eaf0; border-radius: 7px; font-size: 12.5px; font-weight: 600; color: #6b7280; background: #fff; text-decoration: none; transition: all 0.15s; }
.gl-act-view:hover { border-color: #900C27; color: #900C27; background: #f0f5f4; text-decoration: none; }

/* Empty */
.gl-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 80px 0; color: #9ca3af; text-align: center; }
.gl-empty p { margin: 0; font-size: 14px; }

/* Pagination */
.gl-pagination { display: flex; align-items: center; justify-content: center; gap: 4px; padding: 14px; border-top: 1px solid #f0f1f3; }
.gl-page-btn { min-width: 32px; height: 32px; padding: 0 8px; border-radius: 7px; border: 1.5px solid #e8eaf0; background: #fff; font-size: 13px; color: #374151; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.12s; }
.gl-page-btn:hover:not(:disabled) { border-color: #a7c4be; background: #f0f5f4; }
.gl-page-btn.active { background: #900C27; border-color: #900C27; color: #fff; font-weight: 700; }
.gl-page-btn:disabled { opacity: 0.38; cursor: not-allowed; }

/* ── Bottom analytics grid ── */
.gl-bottom-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 32px; }
.gl-bottom-card { background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px; overflow: hidden; }
.gl-bottom-card-header { display: flex; align-items: center; gap: 8px; padding: 14px 18px; border-bottom: 1px solid #f5f6f8; font-size: 13px; font-weight: 700; color: #374151; }
.gl-bottom-card-body { padding: 4px 0; }

/* Compact table (top customers) */
.gl-compact-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.gl-compact-table thead th { padding: 9px 16px; font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; border-bottom: 1px solid #f5f6f8; }
.gl-compact-table thead th.th-rank { width: 36px; text-align: center; }
.gl-compact-table thead th.th-center { text-align: center; }
.gl-compact-table thead th.th-right { text-align: right; }
.gl-compact-table tbody tr { border-bottom: 1px solid #f5f6f8; transition: background 0.1s; }
.gl-compact-table tbody tr:last-child { border-bottom: none; }
.gl-compact-table tbody tr:hover { background: #fafafa; }
.gl-compact-table tbody td { padding: 10px 16px; vertical-align: middle; }
.gl-compact-table tbody td.td-rank { text-align: center; }
.gl-compact-table tbody td.td-center { text-align: center; }
.gl-compact-table tbody td.td-right { text-align: right; }

.gl-rank { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; font-size: 11px; font-weight: 700; background: #f3f4f6; color: #6b7280; }
.gl-rank-1 { background: #fef3c7; color: #92400e; }
.gl-rank-2 { background: #f1f5f9; color: #475569; }
.gl-rank-3 { background: #fff1e6; color: #9a3412; }
.gl-top-cust-name { font-size: 13px; font-weight: 600; color: #111827; text-decoration: none; }
.gl-top-cust-name:hover { color: #900C27; text-decoration: underline; }
.gl-top-cust-phone { font-size: 11.5px; color: #9ca3af; margin-top: 1px; }

/* Tier breakdown */
.gl-tier-row { padding: 11px 18px; border-bottom: 1px solid #f5f6f8; }
.gl-tier-row:last-child { border-bottom: none; }
.gl-tier-row-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 7px; }
.gl-tier-badge-sm { display: inline-block; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.gl-tier-count { font-size: 12px; color: #6b7280; }
.gl-progress-track { height: 6px; background: #f0f1f3; border-radius: 3px; overflow: hidden; }
.gl-progress-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }
.gl-prog-platinum { background: #1e293b; }
.gl-prog-gold { background: #d97706; }
.gl-prog-silver { background: #94a3b8; }
.gl-prog-default { background: #900C27; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-branch-title { font-size: 18px; }

  /* Stats strip — 2×2 grid, no card */
  .gl-stats-strip {
    display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
    background: transparent; border: none; padding: 0; margin-bottom: 14px;
  }
  .gl-stat-divider { display: none; }
  .gl-stat-chip {
    padding: 10px 12px; background: #fff; border: 1.5px solid #f0f1f3;
    border-radius: 10px; height: 72px; box-sizing: border-box; justify-content: space-between;
  }
  .gl-stat-num { font-size: 17px; flex: 1; align-items: flex-start; }
  .gl-stat-lbl { margin-top: auto; }

  /* Filter bar — stack to column */
  .gl-filter-bar { flex-direction: column; align-items: stretch; gap: 8px; }
  .gl-search-wrap { min-width: unset; }
  .gl-filter-select { width: 100%; }
  .gl-filter-clear { justify-content: center; }

  /* Table — hide Contact (2), Orders (4), Last Active (6) */
  .gl-table thead th:nth-child(2),
  .gl-table tbody td:nth-child(2),
  .gl-table thead th:nth-child(4),
  .gl-table tbody td:nth-child(4),
  .gl-table thead th:nth-child(6),
  .gl-table tbody td:nth-child(6) { display: none; }

  .gl-table thead th,
  .gl-table tbody td { padding: 10px 10px; }
  .gl-avatar { width: 32px; height: 32px; }
  .gl-cust-name { font-size: 12.5px; }
  .gl-cust-since { display: none; }

  /* Bottom analytics — single column */
  .gl-bottom-grid { grid-template-columns: 1fr; gap: 12px; }
}
</style>

<style>
/* Dark mode */
body.dark-only .gl-branch-title { color: #f3f4f6; }
body.dark-only .gl-branch-breadcrumb { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a { color: #6b7280; }
body.dark-only .gl-branch-breadcrumb a:hover { color: #d1d5db; }

body.dark-only .gl-stats-strip { background: #262932; border-color: #323644; }
body.dark-only .gl-stat-num { color: #f3f4f6; }
body.dark-only .gl-stat-divider { background: #323644; }
body.dark-only .gl-skel-num { background: #2d3249; }

body.dark-only .gl-search-input { background: #262932; border-color: #2d3249; color: #e5e7eb; }
body.dark-only .gl-search-input::placeholder { color: #4b5563; }
body.dark-only .gl-filter-select { background: #262932; border-color: #2d3249; color: #e5e7eb; }

body.dark-only .gl-skel-row { border-color: #2a2f46; }
body.dark-only .gl-skel-avatar, body.dark-only .gl-skel-line, body.dark-only .gl-skel-badge, body.dark-only .gl-skel-btn-view { background: #2d3249; }

body.dark-only .gl-table-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-table thead tr { background: #252837; }
body.dark-only .gl-table thead th { color: #6b7280; }
body.dark-only .gl-table tbody tr { border-color: #2a2f46; }
body.dark-only .gl-table tbody tr:hover { background: #252837; }
body.dark-only .gl-table tbody td { color: #e5e7eb; }
body.dark-only .gl-avatar-placeholder { background: #2d3249; color: #6b7280; }
body.dark-only .gl-cust-name { color: #f3f4f6; }
body.dark-only .gl-contact-phone { color: #d1d5db; }
body.dark-only .gl-orders-count { color: #f3f4f6; }
body.dark-only .gl-spent-amount { color: #f3f4f6; }
body.dark-only .gl-tier-none { background: #2d3249; color: #6b7280; }
body.dark-only .gl-tier-silver { background: #2d3249; color: #94a3b8; }
body.dark-only .gl-act-view { border-color: #2d3249; background: #1e2130; color: #9ca3af; }
body.dark-only .gl-act-view:hover { border-color: #900C27; color: #4ade80; background: rgba(144,12,39,0.1); }

body.dark-only .gl-pagination { border-color: #2d3249; }
body.dark-only .gl-page-btn { border-color: #2d3249; background: #1e2130; color: #d1d5db; }
body.dark-only .gl-page-btn:hover:not(:disabled) { background: rgba(144,12,39,0.1); border-color: #900C27; }

body.dark-only .gl-bottom-card { background: #1e2130; border-color: #2d3249; }
body.dark-only .gl-bottom-card-header { color: #d1d5db; border-color: #2d3249; }
body.dark-only .gl-compact-table thead th { color: #6b7280; border-color: #2d3249; }
body.dark-only .gl-compact-table tbody tr { border-color: #2a2f46; }
body.dark-only .gl-compact-table tbody tr:hover { background: #252837; }
body.dark-only .gl-top-cust-name { color: #f3f4f6; }
body.dark-only .gl-tier-row { border-color: #2a2f46; }
body.dark-only .gl-tier-count { color: #9ca3af; }
body.dark-only .gl-progress-track { background: #2d3249; }

@media (max-width: 767px) {
  body.dark-only .gl-stats-strip { background: transparent !important; border-color: transparent !important; }
  body.dark-only .gl-stat-chip { background: #262932; border-color: #323644; }
}
</style>
